/**
 * Advanced workflow scheduling (v2) — pure, framework-agnostic logic.
 *
 * This module is the SHARED REFERENCE for how a scenario's activity window is
 * evaluated. The NestJS back-end mirrors the same logic (`evaluateSchedule`)
 * so the front-end preview and the back-end entry gate always agree.
 *
 * Design goals:
 *  - PURE: no Vue, no i18n, no `Date` inside the matching core (the core works
 *    on already-extracted primitives → portable to `moment-timezone` on the
 *    back). A thin `Date`-based wrapper is provided for front-end previews.
 *  - RETRO-COMPATIBLE: `migrateLegacyDateRanges` turns the legacy `date_ranges`
 *    array into a v2 config for display, and `buildLegacyDateRangesMirror`
 *    turns a v2 config back into a best-effort `date_ranges` array so a back
 *    that only understands v1 keeps enforcing the date part.
 *
 * See `shopimind-nestjs/WORKFLOW_SCHEDULING_V2_PLAN_2026-07-10.html`.
 */

import type {
  FlowScheduleConfig,
  FlowScheduleRule,
  FlowScheduleDate,
  FlowScheduleTime,
  FlowSettingsDateRange,
} from '@/types/flow-settings';
import { FLOW_SCHEDULE_VERSION } from '@/types/flow-settings';

/* ────────────────────────────────────────────────────────────────────────
 * Factory / (de)serialisation
 * ──────────────────────────────────────────────────────────────────────── */

/** A fresh, enabled v2 config with no rules (= active everywhere). */
export const createEmptyScheduleConfig = (): FlowScheduleConfig => ({
  version: FLOW_SCHEDULE_VERSION,
  enabled: true,
  timezone_mode: 'shop',
  rules: [],
});

/**
 * Parse a `schedule_config` JSON string. Returns `null` when the string is
 * empty / unparseable / not a v2 config, so callers can fall back to the
 * legacy `date_ranges` path.
 */
export const parseScheduleConfig = (
  raw: string | null | undefined,
): FlowScheduleConfig | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      !parsed
      || typeof parsed !== 'object'
      || !Array.isArray(parsed.rules)
      // NaN-safe: a missing/older version falls back to the legacy path.
      || !(Number(parsed.version) >= FLOW_SCHEDULE_VERSION)
    ) {
      return null;
    }
    return {
      version: FLOW_SCHEDULE_VERSION,
      enabled: parsed.enabled !== false,
      timezone_mode: parsed.timezone_mode ?? 'shop',
      rules: (parsed.rules as FlowScheduleRule[]).filter(Boolean),
    };
  } catch {
    return null;
  }
};

export const serializeScheduleConfig = (config: FlowScheduleConfig): string => JSON.stringify(config);

/* ────────────────────────────────────────────────────────────────────────
 * Evaluation core (pure, primitive-based → portable to the back)
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * A point in time already resolved into the reference timezone, decomposed
 * into the exact primitives the matcher needs. The back builds this from
 * `moment.tz(shopTimezone)`; the front from a JS `Date`.
 */
export interface ScheduleInstant {
  /** ISO calendar date `YYYY-MM-DD`. */
  isoDate: string;
  /** ISO local date-time `YYYY-MM-DDTHH:mm` (for continuous "once" windows). */
  isoDateTime: string;
  /** Month-day `MM-DD`. */
  monthDay: string;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  /** Minutes since midnight, 0 … 1439. */
  minutes: number;
}

/**
 * Normalise a date bound to a comparable `…THH:mm` form. A date-only bound
 * (legacy / migrated) becomes the very start (00:00) or very end (23:59) of
 * that day, preserving the old day-grain "active all day" behaviour. Applies
 * to "once" (`YYYY-MM-DD[THH:mm]`) and "yearly" (`MM-DD[THH:mm]`) bounds.
 */
const normaliseBound = (raw: string, fallbackTime: '00:00' | '23:59'): string => (raw.includes('T') ? raw : `${raw}T${fallbackTime}`);

const pad2 = (n: number | string): string => String(n).padStart(2, '0');

const hhmmToMinutes = (hm: string): number => {
  const [h, m] = String(hm).split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
};

/** Does `instant` fall inside a time-of-day window? Handles midnight wrap. */
export const timeMatches = (time: FlowScheduleTime, minutes: number): boolean => {
  const start = hhmmToMinutes(time.start);
  const end = hhmmToMinutes(time.end);
  // Degenerate/equal window ⇒ treat as the whole day (the UI removes `time`
  // entirely for "all day", so an explicit equal window is not a 0-length one).
  if (start === end) return true;
  if (start < end) return minutes >= start && minutes <= end;
  // start > end ⇒ window wraps past midnight (e.g. 22:00 → 02:00)
  return minutes >= start || minutes <= end;
};

/** Does `instant` fall inside a date window? Minute grain; yearly wraps. */
export const dateMatches = (date: FlowScheduleDate, instant: ScheduleInstant): boolean => {
  if (date.mode === 'once') {
    // Continuous window between two absolute moments. Bounds are `YYYY-MM-DD`
    // (date-only ⇒ whole day) or `YYYY-MM-DDTHH:mm`; both compare correctly
    // lexicographically once normalised to the same fixed-width format.
    const a = normaliseBound(date.start, '00:00');
    const b = normaliseBound(date.end, '23:59');
    const lo = a <= b ? a : b;
    const hi = a <= b ? b : a;
    return instant.isoDateTime >= lo && instant.isoDateTime <= hi;
  }
  // yearly: `MM-DD` (whole day) or `MM-DDTHH:mm` bounds — a continuous window
  // recurring every year. Day-only bounds normalise to 00:00 / 23:59, which is
  // exactly the historical day-grain behaviour. If start > end the window
  // wraps across the year boundary (e.g. 12-15 → 01-10).
  const start = normaliseBound(date.start, '00:00');
  const end = normaliseBound(date.end, '23:59');
  const monthDayTime = instant.isoDateTime.slice(5); // MM-DDTHH:mm
  if (start <= end) {
    return monthDayTime >= start && monthDayTime <= end;
  }
  return monthDayTime >= start || monthDayTime <= end;
};

/** Empty/absent weekday list = every day. */
export const weekdayMatches = (
  weekdays: number[] | null | undefined,
  weekday: number,
): boolean => {
  if (!weekdays || weekdays.length === 0) return true;
  return weekdays.includes(weekday);
};

/** A rule matches when every PRESENT constraint matches. */
export const ruleMatches = (rule: FlowScheduleRule, instant: ScheduleInstant): boolean => {
  if (rule.date && !dateMatches(rule.date, instant)) return false;
  if (!weekdayMatches(rule.weekdays, instant.weekday)) return false;
  if (rule.time && !timeMatches(rule.time, instant.minutes)) return false;
  return true;
};

/**
 * Core decision: is the workflow active at `instant`?
 *  1. disabled / no rules ⇒ active (no restriction).
 *  2. any matching EXCLUDE rule ⇒ inactive (exclusion wins).
 *  3. no include rules ⇒ active (excludes just carve holes).
 *  4. otherwise ⇒ active iff at least one INCLUDE rule matches.
 */
export const evaluateScheduleAt = (
  config: FlowScheduleConfig | null | undefined,
  instant: ScheduleInstant,
): boolean => {
  if (!config || config.enabled === false || !config.rules || config.rules.length === 0) {
    return true;
  }
  const includes = config.rules.filter((r) => r.effect === 'include');
  const excludes = config.rules.filter((r) => r.effect === 'exclude');

  if (excludes.some((r) => ruleMatches(r, instant))) return false;
  if (includes.length === 0) return true;
  return includes.some((r) => ruleMatches(r, instant));
};

/** Build a `ScheduleInstant` from a JS `Date` (uses the Date's local fields). */
export const instantFromDate = (d: Date): ScheduleInstant => {
  const jsDay = d.getDay(); // 0 = Sun … 6 = Sat
  const isoDate = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return {
    isoDate,
    isoDateTime: `${isoDate}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    monthDay: `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    weekday: jsDay === 0 ? 7 : jsDay, // → ISO 1..7
    minutes: d.getHours() * 60 + d.getMinutes(),
  };
};

/** Front-end convenience: evaluate against a `Date` (defaults to "now"). */
export const evaluateSchedule = (
  config: FlowScheduleConfig | null | undefined,
  now: Date = new Date(),
): boolean => evaluateScheduleAt(config, instantFromDate(now));

/* ────────────────────────────────────────────────────────────────────────
 * Legacy `date_ranges` (v1) ⇆ `schedule_config` (v2) bridging
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Decode a single legacy date string into a normalised shape.
 * Legacy formats seen in the wild:
 *   - `MM-DD`        → "every year" (masterV4 + v1 "everyYear" mode)
 *   - `MM-DD-YYYY`   → "current year" (v1 "current" mode written by the front)
 *   - `YYYY-MM-DD`   → tolerated defensively (never produced by the front, but
 *                      the back's buggy parser assumed this shape)
 */
const decodeLegacyDate = (
  raw: string,
): { mode: 'once'; iso: string } | { mode: 'yearly'; monthDay: string } | null => {
  if (typeof raw !== 'string') return null;
  const parts = raw.split('-');
  if (parts.length === 3) {
    let year: string;
    let month: string;
    let day: string;
    if (parts[0].length === 4) {
      [year, month, day] = parts; // YYYY-MM-DD
    } else {
      [month, day, year] = parts; // MM-DD-YYYY (front "current" format)
    }
    return { mode: 'once', iso: `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}` };
  }
  if (parts.length === 2) {
    return { mode: 'yearly', monthDay: `${pad2(parts[0])}-${pad2(parts[1])}` };
  }
  return null;
};

const isoToMonthDay = (iso: string): string => {
  const [, month, day] = iso.split('-');
  return `${pad2(month)}-${pad2(day)}`;
};

/** `YYYY-MM-DD` → `MM-DD-YYYY` (the legacy "current year" storage format). */
const isoToLegacyCurrent = (iso: string): string => {
  const [year, month, day] = iso.split('-');
  return `${pad2(month)}-${pad2(day)}-${String(year).padStart(4, '0')}`;
};

/**
 * Migrate the legacy `date_ranges` JSON into a v2 config (for display / editing
 * of a pre-existing scenario). Each legacy range → one date-only rule, so an
 * old scenario is faithfully restored in the new UI. Time/weekday dimensions
 * are simply absent (whole day, every day), exactly matching the old behaviour.
 *
 * `inclusion_type` is treated as EXCLUDE only when strictly `'exclude'` — the
 * same rule the back applies — so anything else (incl. legacy masterV4 data
 * with no `inclusion_type`) becomes an INCLUDE rule.
 */
export const migrateLegacyDateRanges = (
  dateRangesJson: string | null | undefined,
): FlowScheduleConfig => {
  const config = createEmptyScheduleConfig();

  let ranges: FlowSettingsDateRange[] = [];
  try {
    const parsed = JSON.parse(dateRangesJson || '[]');
    if (Array.isArray(parsed)) ranges = parsed;
  } catch {
    ranges = [];
  }

  ranges.forEach((range, index) => {
    if (!range || typeof range.date_start !== 'string' || typeof range.date_end !== 'string') {
      return;
    }
    const start = decodeLegacyDate(range.date_start);
    const end = decodeLegacyDate(range.date_end);
    if (!start || !end) return;

    let date: FlowScheduleDate;
    if (start.mode === 'once' && end.mode === 'once') {
      date = { mode: 'once', start: start.iso, end: end.iso };
    } else {
      // Mixed/other → normalise to a yearly (month-day) window.
      const startMd = start.mode === 'yearly' ? start.monthDay : isoToMonthDay(start.iso);
      const endMd = end.mode === 'yearly' ? end.monthDay : isoToMonthDay(end.iso);
      date = { mode: 'yearly', start: startMd, end: endMd };
    }

    config.rules.push({
      id: `legacy-${index}`,
      effect: range.inclusion_type === 'exclude' ? 'exclude' : 'include',
      date,
    });
  });

  return config;
};

/**
 * Best-effort reverse mirror: v2 config → legacy `date_ranges` array, so a
 * back-end that only understands v1 keeps enforcing the DATE part during the
 * rollout window.
 *
 * Only the date dimension survives (v1 cannot express weekdays or hours):
 *  - rules WITHOUT a date (pure weekly windows) are skipped — the old back
 *    cannot represent them at all;
 *  - rules WITH a date keep their date window; any weekday/time restriction is
 *    dropped in the mirror (the old back enforces a slightly broader window,
 *    the new back enforces the exact rule via `schedule_config`).
 */
export const buildLegacyDateRangesMirror = (
  config: FlowScheduleConfig,
): FlowSettingsDateRange[] => {
  if (!config || !Array.isArray(config.rules)) return [];
  const mirror: FlowSettingsDateRange[] = [];

  config.rules.forEach((rule) => {
    if (!rule.date) return; // pure weekly rule → not representable in v1
    const inclusion: FlowSettingsDateRange['inclusion_type'] = rule.effect === 'exclude' ? 'exclude' : 'include';

    if (rule.date.mode === 'once') {
      // The legacy v1 back is day-grain: drop any time component in the mirror.
      mirror.push({
        date_start: isoToLegacyCurrent(rule.date.start.split('T')[0]),
        date_end: isoToLegacyCurrent(rule.date.end.split('T')[0]),
        year_option: 'current',
        inclusion_type: inclusion,
      });
    } else {
      mirror.push({
        // Same day-grain rule for yearly bounds: `MM-DDTHH:mm` → `MM-DD`.
        date_start: rule.date.start.split('T')[0],
        date_end: rule.date.end.split('T')[0],
        year_option: 'everyYear',
        inclusion_type: inclusion,
      });
    }
  });

  return mirror;
};

/** Does the config carry at least one rule the legacy back can enforce? */
export const hasEnforceableRules = (config: FlowScheduleConfig): boolean => config.rules.length > 0;
